"use client";

import Link from "next/link";
import clsMerge from "../util/clsMerge";
import Button, {ButtonVariantProps, buttonStyles} from "./Button";
import {ButtonSizes, ButtonVariant} from "./ButtonVariants";
import {useButtonInputTracking} from "./useButtonInputTracking";

interface AnchorButtonProps
	extends ButtonVariantProps,
		React.AnchorHTMLAttributes<HTMLAnchorElement> {
	isDisabled?: boolean;
	shouldUseNextRouter?: boolean;
}

const AnchorButton: React.FC<AnchorButtonProps> = ({
	children,
	className,
	variant = ButtonVariant.PRIMARY,
	size = ButtonSizes.LARGE,
	isDisabled,
	shouldUseNextRouter = true,
	onClick: onAnchorClick,
	...props
}) => {
	const {trackedOnClick} = useButtonInputTracking({
		onClick: (event) =>
			onAnchorClick?.(event as React.MouseEvent<HTMLAnchorElement, MouseEvent>),
		props: {href: "/", ...props},
	});

	if (isDisabled) {
		/**
		 * For the most part we want to avoid using the isDisabled flag, but if
		 * necessary, return a disabled button
		 * (https://css-tricks.com/how-to-disable-links/)
		 */
		return (
			<Button variant={variant} size={size} className={className} disabled>
				{children}
			</Button>
		);
	}

	const mergedClassName = clsMerge(buttonStyles({variant, size}), className);

	if (shouldUseNextRouter) {
		const {href = "/", ...linkProps} = props;
		return (
			<Link
				className={mergedClassName}
				href={href}
				onClick={trackedOnClick}
				{...linkProps}
			>
				{children}
			</Link>
		);
	}

	return (
		<a className={mergedClassName} onClick={trackedOnClick} {...props}>
			{children}
		</a>
	);
};

export default AnchorButton;
